"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import React from "react";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { config } from "@/lib/config";

export default function Contact({
  children,
  content,
  title,
  hidden,
  messageLabel,
  messagePlaceholder,
}: {
  children?: React.ReactNode;
  content?: React.ReactNode;
  title: string;
  hidden: string;
  messageLabel?: string;
  messagePlaceholder?: string;
}) {
  const formSchema = z.object({
    name: z
      .string()
      .min(2, { message: "A név legalább 2 betű legyen" })
      .max(50, { message: "A név maximum 50 betű lehet" }),
    email: z.string().email({ message: "Nem megfelelő e-mail cím" }),
    message: z
      .string()
      .min(10, { message: "Az üzenet hossza legalább 10 betű legyen" })
      .max(2048, { message: "Az üzenet hossza maximum 2048 betű lehet" }),
    hidden: z.string().optional(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
      hidden: hidden,
    },
  });

  // 2. Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (values && values.name && values.email && values.message) {
      try {
        const submitData = {
          name: values.name,
          email: values.email,
          message: values.message,
          hidden: hidden ? values.hidden : "",
        };

        const res = await fetch(`${config.basePath}/api/`, {
          method: "POST",
          body: JSON.stringify(submitData),
          headers: {
            "content-type": "application/json",
          },
        });
        if (res.ok) {
        } else {
          console.error("Oops! Something is wrong.");
        }
      } catch (error) {
        console.log(error);
      }
      toast.success("Az üzenet elküldve");
      form.reset();
    }
    console.log(values);
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full space-y-8 p-8"
      >
        {children}
        <h1 className="w-full text-center text-2xl font-bold">
          {title ? title : "Kapcsolat"}
        </h1>
        {content && (
          <div className="w-full">
            {content}
          </div>
        )}
        <FormField
          control={form.control}
          name="hidden"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input type="hidden" placeholder="" {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-2xl font-bold">Név</FormLabel>
              <FormControl>
                <Input placeholder="Név" {...field} />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-2xl font-bold">E-mail</FormLabel>
              <FormControl>
                <Input placeholder="E-mail cím" {...field} />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-2xl font-bold">{`${messageLabel||"Üzenet"}`}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={`${messagePlaceholder||"Üzenet"}`}
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="mx-auto w-full bg-red-900 text-2xl font-bold hover:bg-red-600"
        >
          Küldés
        </Button>
      </form>
    </Form>
  );
}
